import React, { useEffect, useState, useCallback, useContext } from 'react';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Tooltip from '@amzn/meridian/tooltip';
import Icon from '@amzn/meridian/icon';
import bulbTokens from '@amzn/meridian-tokens/base/icon/bulb';
import RenderCheckboxesFromConfig, {
  CheckboxItem,
} from '../../Util/Form/RenderCheckboxesFromConfig';
import styles from './CCXDetailsTab.module.css';
import { FormattedMessage } from 'react-intl';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import StarRating from '../../Util/generic/StarRating';
import Pagination from '@amzn/meridian/pagination';
import moment from 'moment';
import Link from '@amzn/meridian/link';
import Text from '@amzn/meridian/text';
import { getDateRangeFromTimestamp, renderUrl, urls } from '../../Util';
import { ExtendedUser, Profile } from '../../Types';
import PerformancePortalContext from '../PerformancePortalContext';
import { useNavigate } from 'react-router';
import { SortDirection } from '../../Types/SortDirection';
import Loader from '@amzn/meridian/loader';
import ContactListeningModal from '../ContactListening/ContactListeningModal';
import {
  ContactData,
  defaultContactData,
} from '../../api/ContactListening/schema';
import DataState from '../../Types/DataState';
import { QueryVariablesContactListeningData } from '../../api/Naws';
import { isFeatureEnabled, Feature } from '../../config/FeaturesConfig';
import ContactListening from '../ContactListening/ContactListening';
import { parseIdWithAlias } from '../Dashboard/DashboardComponents/HmdDetails/helper';
import { ContactDetail } from '../Dashboard/DashboardComponents/HmdDetails/types';
import Column from '@amzn/meridian/column';
import { ContactStatusOptionsObject } from '../../api/CoachingForm/schema';
import { hasStarRatingVariance } from './helper';

interface ICCXOverallTabProps {
  lastUpdated: Date | undefined;
  activeFilters?: Record<string, string>[];
  dataset: CCXOverallTabTableDataItem[];
  userID: string;
  profile: Profile;
  selectedRating: string | null | undefined;
  setSelectedRating: (args: any) => void;
  isTM: boolean;
  loading: boolean;
  cscEntityPrefix: string;
  lastCheckedAll: boolean;
  setLastCheckedAll: (args: any) => void;
  renderCount: number;
  renderedFilterCount?: number;
  redirectURLParams?: string;
}

type NullableString = string | undefined | null;
type ContactStatusType = 'DRAFT' | 'REVIEWED' | 'DELIVERED';

export type CCXOverallTabTableDataItem = {
  userID: string;
  starRating?: number;
  workCategory: string;
  issueCode: string;
  communicationDate: string;
  responseDate: string;
  contactID: string;
  status?: string;
  ccxOverall: NullableString;
  ccxUnderstand: NullableString;
  ccxEmpathy: NullableString;
  ccxCourtesy: NullableString;
  ccxEfficiency: NullableString;
  ccxKnowledgeable: NullableString;
  ccxHelpfulness: NullableString;
  encryptedRapRepeatContactId: NullableString;
  rapRepeatContactId: NullableString;
  marketplaceName: string;
  contactStatus?: ContactStatusType;
};

type TableHeaderItem = {
  defaultMessage: string;
  translationId: string;
};

interface ICCXOverallTabState {
  checkboxes: CheckboxItem[];
  tableData?: CCXOverallTabTableDataItem[];
  tableHeaders?: TableHeaderItem[];
  filteredData?: CCXOverallTabTableDataItem[];
}

const itemsPerPage = 10;
const dateFormat = 'YYYY-MM-DD HH:mm [UTC]';

// will be replaced by API
const checkboxIDs = {
  all: 'cs_perfportal_ccx_details_overall_tab_all_checkbox',
  one: 'cs_perfportal_ccx_details_overall_tab_1_star_checkbox',
  two: 'cs_perfportal_ccx_details_overall_tab_2_star_checkbox',
  three: 'cs_perfportal_ccx_details_overall_tab_3_star_checkbox',
  four: 'cs_perfportal_ccx_details_overall_tab_4_star_checkbox',
  five: 'cs_perfportal_ccx_details_overall_tab_5_star_checkbox',
};

const calculateDatasetPercentage = (
  dataset: CCXOverallTabTableDataItem[],
  starRating: number
) => {
  const num = Math.floor(
    (dataset.filter(i => i.starRating === starRating).length / dataset.length) *
      100
  );
  return isNaN(num) ? 0 : num;
};

const sortConfig: {
  [key: string]: keyof CCXOverallTabTableDataItem | undefined;
} = {
  Alias: 'userID',
  'Star Rating': 'starRating',
  'Work Category': 'workCategory',
  'Issue Code': 'issueCode',
  'Contact Date': 'communicationDate',
  'Response Date': 'responseDate',
  'Contact ID': 'contactID',
  Action: undefined,
  'Repeat Contact ID': 'encryptedRapRepeatContactId',
  'Contact Status': 'contactStatus',
};

const mapProfileIDsToNames = (profile: Profile): { [key: string]: string } => {
  const userMap: { [key: string]: string } = {};
  const mapUserIdToAlias = (item: ExtendedUser) => {
    userMap[item.userID] = item.alias;
  };
  profile.directReports.forEach(mapUserIdToAlias);
  profile.sharedProfileCoachees.forEach(mapUserIdToAlias);
  profile.sharedProfileCoaches.forEach(mapUserIdToAlias);
  profile.managers.forEach(mapUserIdToAlias);
  return userMap;
};

const CCXDetailsTab: React.FC<ICCXOverallTabProps> = ({
  loading,
  lastUpdated,
  dataset,
  userID,
  profile,
  selectedRating,
  setSelectedRating,
  isTM,
  cscEntityPrefix,
  lastCheckedAll,
  setLastCheckedAll,
  renderCount,
  redirectURLParams,
}: ICCXOverallTabProps) => {
  const contactLinksEnabled = isFeatureEnabled(Feature.CONTACT_LINKS);
  const contactListeningEnabled = isFeatureEnabled(Feature.CONTACT_LISTENING);

  const defaultContactDateRange = getDateRangeFromTimestamp();
  const [contactStartDate] = useState(defaultContactDateRange.contactStartDate);
  const [contactEndDate] = useState(defaultContactDateRange.contactEndDate);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingContactId, setRecordingContactId] = useState('');
  const [contactListeningData, setContactListeningData] =
    useState<ContactData>(defaultContactData);
  const [contactListeningDataState, setContactListeningDataState] =
    useState<string>(DataState.INITIALIZED);
  const [contactListeningParams, setContactListeningParams] =
    useState<QueryVariablesContactListeningData>();
  const [tabState, setTabState] = useState<ICCXOverallTabState | undefined>(
    undefined
  );

  const { performancePortalContextValue, setPerformancePortalContextValue } =
    useContext(PerformancePortalContext);
  const navigate = useNavigate();

  const [profileMap, setProfileMap] = useState(mapProfileIDsToNames(profile));
  const [loaded, setLoaded] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;

  const [sortColumn, setSortColumn] =
    useState<keyof CCXOverallTabTableDataItem>('starRating');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.ASCENDING
  );
  const onSort = useCallback(
    ({
      sortColumn: sC,
      sortDirection: sD,
    }: {
      sortColumn: keyof CCXOverallTabTableDataItem;
      sortDirection: SortDirection;
    }) => {
      setSortDirection(sD);
      setSortColumn(sC);
    },
    []
  );

  const getSortedData = (
    dataset = tabState?.filteredData
  ): CCXOverallTabTableDataItem[] => {
    const qualifier = sortDirection === SortDirection.ASCENDING ? -1 : 1;
    const newData: CCXOverallTabTableDataItem[] = (dataset ?? []).sort(
      (a: any, b: any) => {
        if (sortColumn === 'userID') {
          // Special handling for alias sorting
          const aliasA = (getAlias(a) || '').toLowerCase();
          const aliasB = (getAlias(b) || '').toLowerCase();
          return aliasA > aliasB
            ? 1 * qualifier
            : aliasA === aliasB
              ? 0
              : -1 * qualifier;
        }
        // Normal sorting for other columns
        return a[sortColumn] > b[sortColumn]
          ? 1 * qualifier
          : a[sortColumn] === b[sortColumn]
            ? 0
            : -1 * qualifier;
      }
    );
    return newData;
  };

  const handleStarFilterData = (
    state: ICCXOverallTabState | undefined = tabState,
    allChecked = !!tabState?.checkboxes?.find(
      c => c.checked && c.labelId === checkboxIDs.all
    )
  ) => {
    if (state?.tableData) {
      // when checkboxes change, we also need to refilter the data
      const newData = tabState?.tableData;
      if (allChecked || lastCheckedAll) {
        setTabState({ ...state, filteredData: newData });
        return;
      }

      // extract the value from the id.
      const activeFilters: number[] = state?.checkboxes
        ?.filter(c => c.checked)
        ?.map(({ labelId }) => {
          const parts = labelId.split('cs_perfportal_ccx_details_overall_tab_');
          const partWeNeed = parts?.[1];
          // return the first character, which will always be the number
          return +partWeNeed[0];
        });

      setSelectedRating(activeFilters.join(','));

      setTabState({
        ...state,
        filteredData:
          activeFilters?.length > 0
            ? newData?.filter(i => activeFilters.includes(i?.starRating ?? -1))
            : tabState?.tableData,
      });
    }
  };

  const handleCheckboxChange = useCallback(
    (labelId: string, checked: boolean) => {
      if (labelId === checkboxIDs.all) {
        checked = true;
      }
      const allChecked = labelId === checkboxIDs.all && checked;
      let newState = {
        ...tabState,
        checkboxes: (tabState?.checkboxes ?? [])?.map(c => {
          // if all is selected, we need to unselect the rest.
          if (allChecked && c.labelId !== checkboxIDs.all) {
            c.checked = false;
          } else if (c.labelId === labelId) {
            c.checked = allChecked || checked;
          }
          return c;
        }),
      };

      if (!allChecked) {
        newState.checkboxes = newState.checkboxes.map(c =>
          c.labelId === checkboxIDs.all ? { ...c, checked: false } : c
        );
      }
      handleStarFilterData(newState, allChecked);
    },
    [tabState, setTabState]
  );

  const CLClickstream = {
    slotID: 'open-contact-listening-modal',
    tag1: 'Dashboard',
    tag2: 'Historical CCX Details',
    tag3: '',
    tag4: '',
  };

  useEffect(() => {
    if (!loading) {
      handleStarFilterData();
    }
  }, [loading, tabState?.tableData]);

  useEffect(() => {
    const checkboxes = getCheckboxState(loaded);
    handleStarFilterData();
    setTabState({ ...tabState, checkboxes });
  }, []);

  useEffect(() => {
    const checkboxes = getCheckboxState(loaded);
    handleStarFilterData();
    setTabState({ ...tabState, checkboxes });
  }, [selectedRating, dataset, renderCount]);

  useEffect(() => {
    // reset paginator when filters are changed
    setCurrentPage(1);
  }, [tabState?.filteredData]);

  useEffect(() => {
    setProfileMap(mapProfileIDsToNames(profile));
  }, [profile]);

  const getCheckboxState = (loaded: boolean) => {
    const isChecked = (t: string) =>
      !selectedRating
        ? t === 'All'
        : selectedRating.includes(t.substring(0, 1));

    const stateMap: { [key: string]: boolean } | undefined =
      tabState?.checkboxes.reduce((p, c) => {
        return { ...p, [c.labelText]: c.checked };
      }, {});

    const isAllChecked = lastCheckedAll
      ? true
      : !loaded
        ? isChecked('All')
        : (stateMap?.['All'] ?? false);

    const unCheckAll = (val: boolean) => {
      if (val) {
        setLastCheckedAll(false);
      }
      handleStarFilterData();
    };

    const checkboxes: CheckboxItem[] = [
      {
        checked:
          isAllChecked ||
          (tabState?.checkboxes?.every(c => !c.checked) ?? false),
        labelText: 'All',
        labelId: checkboxIDs.all,
        onClick: (val: boolean) => {
          if (isAllChecked || tabState?.checkboxes?.every(c => !c.checked)) {
            return;
          }
          setLastCheckedAll(val);
        },
      },
      {
        checked:
          !loaded && !isAllChecked
            ? isChecked('1 star')
            : (stateMap?.['1 star'] ?? false),
        labelText: `1 star`,
        labelId: checkboxIDs.one,
        onClick: unCheckAll,
        additionalLabelText: `(${calculateDatasetPercentage(dataset, 1)}%)`,
      },
      {
        checked:
          !loaded && !isAllChecked
            ? isChecked('2 star')
            : (stateMap?.['2 star'] ?? false),
        labelText: `2 star`,
        labelId: checkboxIDs.two,
        onClick: unCheckAll,
        additionalLabelText: `(${calculateDatasetPercentage(dataset, 2)}%)`,
      },
      {
        checked:
          !loaded && !isAllChecked
            ? isChecked('3 star')
            : (stateMap?.['3 star'] ?? false),
        labelText: `3 star`,
        labelId: checkboxIDs.three,
        onClick: unCheckAll,
        additionalLabelText: `(${calculateDatasetPercentage(dataset, 3)}%)`,
      },
      {
        checked:
          !loaded && !isAllChecked
            ? isChecked('4 star')
            : (stateMap?.['4 star'] ?? false),
        labelText: `4 star`,
        labelId: checkboxIDs.four,
        onClick: unCheckAll,
        additionalLabelText: `(${calculateDatasetPercentage(dataset, 4)}%)`,
      },
      {
        checked:
          !loaded && !isAllChecked
            ? isChecked('5 star')
            : (stateMap?.['5 star'] ?? false),
        labelText: `5 star`,
        labelId: checkboxIDs.five,
        onClick: unCheckAll,
        additionalLabelText: `(${calculateDatasetPercentage(dataset, 5)}%)`,
      },
    ];
    return checkboxes;
  };

  useEffect(() => {
    const checkboxes = getCheckboxState(loaded);
    const headers = [
      'Alias',
      'Star Rating',
      'Work Category',
      'Issue Code',
      'Contact Date',
      'Response Date',
      'Contact ID',
      'Repeat Contact ID',
    ];

    if (isTM) {
      headers.push('Contact Status');
      headers.push('Action');
    }

    const tableHeaders: TableHeaderItem[] = headers.map(h => ({
      defaultMessage: h,
      translationId: `cs_perfportal_ccx_details_overall_tab_table_header_${h
        .toLowerCase()
        .replace(/ /g, '_')}`,
    }));

    setTabState({
      ...tabState,
      checkboxes,
      tableData: dataset,
      filteredData: getSortedData(dataset),
      tableHeaders,
    });
    setLoaded(true);
  }, [dataset]);

  useEffect(() => {
    if (tabState?.tableData) {
      const newState: ICCXOverallTabState = {
        ...tabState,
        filteredData: getSortedData(),
      } as ICCXOverallTabState;
      setTabState(newState);
    }
  }, [sortColumn, sortDirection]);

  const getCCXDetailsParamsFromDataItem = ({
    ccxCourtesy,
    ccxEfficiency,
    ccxEmpathy,
    ccxHelpfulness,
    ccxKnowledgeable,
    ccxOverall,
    ccxUnderstand,
  }: CCXOverallTabTableDataItem): string => {
    const str = [
      ccxOverall ?? 0,
      ccxEmpathy ?? 0,
      ccxCourtesy ?? 0,
      ccxKnowledgeable ?? 0,
      ccxUnderstand ?? 0,
      ccxHelpfulness ?? 0,
      ccxEfficiency ?? 0,
    ].join(',');
    return str;
  };

  const checkboxes = (
    <RenderCheckboxesFromConfig
      checkboxes={tabState?.checkboxes ?? []}
      styles={{
        parentContainer: styles.checkboxesContainer,
      }}
      onChange={handleCheckboxChange}
    />
  );

  const lastUpdatedByMarkup = (
    <Text>
      <span className={styles.lastUpdatedText}>
        <FormattedMessage
          id='cs_perfportal_ccx_details_overall_tab_last_updated_by'
          defaultMessage='Last updated'
        />
        &nbsp;{moment(lastUpdated).format(dateFormat)}
      </span>
    </Text>
  );

  const redirectToTXMonitoring = (
    data: CCXOverallTabTableDataItem,
    ccxRatings: string
  ) => {
    if (performancePortalContextValue && setPerformancePortalContextValue) {
      const alias = getAlias(data);
      const userIDSplit = data.userID.split(':');
      const userIDToUse =
        performancePortalContextValue?.passedCCXUserID ?? userID;
      setPerformancePortalContextValue({
        ...performancePortalContextValue,
        passedProfile: profile?.directReports?.find(d => d.alias === alias),
        ccxRatings,
        passedCCXUserID: userIDToUse,
      });
      const {
        filterVerticalValue,
        skillValue,
        marketplaceValue,
        segmentValue,
        weekValue,
        channelValue,
      } = performancePortalContextValue?.dashboardFilters ?? {};
      navigate(
        renderUrl(
          `${urls.contactReview}/${userIDSplit?.[1]}/${data.contactID}?ccxRatings=${ccxRatings}&pUserID=${userIDToUse}${redirectURLParams}`
        )
      );
    }
  };
  const idOnly = userID.includes(':') ? userID.split(':')[1] : userID;
  const aliasUrl = renderUrl(`${urls.dashboard}/${idOnly}`);

  const getContactHandlerId = (data: CCXOverallTabTableDataItem) => {
    const { userID } = data;
    const id = userID.includes(':') ? userID.split(':')[1] : userID;
    return `${cscEntityPrefix}:${id}`;
  };

  const getAlias = (data: CCXOverallTabTableDataItem) => {
    if (!profile?.user) {
      return;
    }
    const { userID } = data;
    const aliasArray: Array<{ alias: string }> = parseIdWithAlias(
      [{ userID } as ContactDetail],
      profile?.user,
      profile?.directReports
    );
    return aliasArray?.[0]?.alias ?? '';
  };

  const contactUrl = (data: CCXOverallTabTableDataItem) =>
    `/gp/stores/${data.marketplaceName}/gp/communications/issue-manager?initialCommId=${data.contactID}`;

  const buildIdAndDefaultMessageFromContactStatus = (
    item: CCXOverallTabTableDataItem
  ): { defaultMessage: string; id: string } => {
    let id = 'cs_perfportal_coaching_review';
    let defaultMessage = 'Review';

    switch (item.contactStatus) {
      case ContactStatusOptionsObject.DRAFT:
        id = id + '_edit';
        defaultMessage = 'Edit';
        break;
      case ContactStatusOptionsObject.DELIVERED:
      case ContactStatusOptionsObject.REVIEWED:
        id = id + '_view';
        defaultMessage = 'View';
        break;
    }
    return { defaultMessage, id };
  };

  const getFormattedMessageFromContactStatus = (
    item: CCXOverallTabTableDataItem
  ) => {
    return (
      <FormattedMessage {...buildIdAndDefaultMessageFromContactStatus(item)} />
    );
  };

  const mapContactStatus = (
    status: ContactStatusType | undefined
  ): React.ReactNode | undefined => {
    if (!status) {
      return;
    }

    // capitalize first letter
    const defaultMessage =
      status[0].toUpperCase() +
      status.substring(1, status.length).toLowerCase();

    // determine id based off status
    let id = '';
    switch (status) {
      case ContactStatusOptionsObject.DELIVERED:
        id = 'cs_perfportal_status_delivered';
        break;
      case ContactStatusOptionsObject.DRAFT:
        id = 'cs_perfportal_coaching_status_not_coached';
        break;
      case ContactStatusOptionsObject.REVIEWED:
      default:
        id = 'cs_perfportal_contact_recom_reviewed_status';
        break;
    }

    return <FormattedMessage defaultMessage={defaultMessage} id={id} />;
  };

  const table = (
    <div className={styles.outerTableContainer}>
      <Table
        stickyHeaderRow={true}
        showDividers={true}
        showStripes={true}
        headerRows={1}
        className={styles.dataTableContainer}
        sortColumn={sortColumn}
        sortDirection={
          sortDirection === SortDirection.ASCENDING
            ? SortDirection.ASCENDING
            : SortDirection.DESCENDING
        }
        onSort={onSort}
      >
        <TableRow>
          {tabState?.tableHeaders?.map(h => (
            <TableCell sortColumn={sortConfig[h.defaultMessage]}>
              <FormattedMessage
                id={h.translationId}
                defaultMessage={h.defaultMessage}
              />
            </TableCell>
          ))}
        </TableRow>
        {tabState?.filteredData
          ?.slice(firstVisibleIndex, lastVisibleIndex)
          ?.map(data => {
            const handlerId = getContactHandlerId(data);
            return (
              <TableRow>
                <TableCell>
                  <Link
                    type='secondary'
                    data-csa-c-type='link'
                    data-csa-c-content-id={`open-associate-profile-link-for-${getAlias(
                      data
                    )}`}
                    data-csa-c-is-tm={profile?.directReports?.length > 0}
                    data-csa-c-slot-id='open-associate-profile-link'
                    data-csa-c-profile-id={profile?.user?.userID ?? ''}
                    data-csa-c-username={getAlias(data)}
                    data-csa-c-tag1='Dashboard'
                    data-csa-c-tag2='Historical CCX Details'
                    onClick={() => navigate(aliasUrl)}
                  >
                    {getAlias(data)}
                  </Link>
                </TableCell>
                <TableCell>
                  <StarRating
                    iconStyles={styles.iconStyles}
                    maxAmount={5}
                    fillAmount={data?.starRating ?? 0}
                  />
                </TableCell>
                <TableCell>{data.workCategory}</TableCell>
                <TableCell>{data.issueCode}</TableCell>
                <TableCell>
                  {data.communicationDate
                    ? moment(data.communicationDate).format(dateFormat)
                    : ''}
                </TableCell>
                <TableCell>
                  {data.responseDate
                    ? moment(data.responseDate).format(dateFormat)
                    : ''}
                </TableCell>
                <TableCell>
                  <Row>
                    {(contactLinksEnabled && data.marketplaceName) ||
                    contactListeningEnabled ? (
                      <ContactListening
                        contactID={data.contactID}
                        key={`${data.contactID}-${handlerId}`}
                        setContactListening={setContactListeningData}
                        setContactListeningState={setContactListeningDataState}
                        setRecordingContactId={setRecordingContactId}
                        setShowRecordingModal={setShowRecordingModal}
                        setContactListeningParams={setContactListeningParams}
                        clickstreamTags={{
                          'data-csa-c-slot-id': 'open-contact-listening-link',
                          'data-csa-c-content-id': `open-contact-listening-link-for-${data.contactID}`,
                          'data-csa-c-tag1': 'Dashboard',
                          'data-csa-c-tag2': 'Historical CCX Details',
                          'data-csa-c-action': 'Contact Listening',
                          'data-csa-c-type': 'link',
                          'data-csa-c-is-tm': isTM,
                          'data-csa-c-username': getAlias(data),
                          'data-csa-c-profile-id': userID,
                          'data-csa-c-contactid': data.contactID,
                        }}
                        cscEndpoint={contactUrl(data)}
                        cscTarget='_blank'
                        contactStartDate={contactStartDate}
                        contactEndDate={contactEndDate}
                        contactHandlerId={handlerId}
                        CLClickstream={CLClickstream}
                      />
                    ) : (
                      data.contactID
                    )}
                    {isTM && hasStarRatingVariance(data) && (
                      <Tooltip
                        position='top'
                        title={
                          <FormattedMessage
                            id='cs_perfportal_ccx_details_variance_tooltip'
                            defaultMessage='The customer provided different CCX star ratings across metrics, which usually signals thoughtful feedback. Dive deeper into this contact.'
                          />
                        }
                      >
                        <Icon tokens={bulbTokens} />
                      </Tooltip>
                    )}
                  </Row>
                </TableCell>
                <TableCell>{data.encryptedRapRepeatContactId}</TableCell>
                {isTM && (
                  <>
                    <TableCell>
                      {mapContactStatus(data.contactStatus)}
                    </TableCell>
                    <TableCell>
                      <Link
                        type='secondary'
                        data-csa-c-type='link'
                        data-csa-c-content-id={`historical-ccx-${
                          buildIdAndDefaultMessageFromContactStatus(data)
                            .defaultMessage
                        }-link`}
                        data-csa-c-is-tm={profile?.directReports?.length > 0}
                        data-csa-c-slot-id={`historical-ccx-${
                          buildIdAndDefaultMessageFromContactStatus(data)
                            .defaultMessage
                        }-link-slot`}
                        data-csa-c-profile-id={profile?.user?.userID ?? ''}
                        data-csa-c-username={getAlias(data)}
                        data-csa-c-tag1='Dashboard'
                        data-csa-c-tag2='Historical CCX Details'
                        data-csa-c-action={
                          buildIdAndDefaultMessageFromContactStatus(data)
                            .defaultMessage
                        }
                        onClick={() =>
                          redirectToTXMonitoring(
                            data,
                            getCCXDetailsParamsFromDataItem(data)
                          )
                        }
                      >
                        {getFormattedMessageFromContactStatus(data)}
                      </Link>
                    </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
      </Table>
      <Pagination
        showSkipArrows={true}
        numberOfPages={Math.ceil(
          (tabState?.filteredData?.length ?? 0) / itemsPerPage
        )}
        onChange={setCurrentPage}
        currentPage={currentPage}
      />
    </div>
  );

  return (
    <Box>
      <Row>{checkboxes}</Row>
      <Row>
        {dataset?.length && tabState?.filteredData?.length && loaded && !loading
          ? lastUpdatedByMarkup
          : ''}
      </Row>
      <Row>
        {!loading && loaded ? (
          table
        ) : (
          <Column width='100%' heights='fill' alignmentHorizontal='center'>
            <Loader type='circular' size='large' />
          </Column>
        )}
      </Row>
      <ContactListeningModal
        loadingState={contactListeningDataState}
        showModal={showRecordingModal}
        setShowModal={setShowRecordingModal}
        contactId={recordingContactId}
        contactListeningData={contactListeningData}
        contactListeningParams={contactListeningParams}
        CLClickstream={{
          ...CLClickstream,
          tag5: 'Contact Listening Modal',
        }}
      />
    </Box>
  );
};

export default CCXDetailsTab;
